import {
  AxiosError,
  AxiosRequestConfig,
  AxiosResponse
} from "axios";
import React from "react";
import { useDispatch } from "redux-react-hook";
import * as Sentry from "@sentry/react";
import { setup } from "axios-cache-adapter";
import localforage from "localforage";
// @ts-ignore
import memoryDriver from "localforage-memoryStorageDriver";
import * as queryString from "query-string";

import createDebug from "src/library/utils/debug";
import apiError, { IApiError } from "src/library/utils/errors/apiError";
import getHeaders from "src/utils/aws-amplify/getHeaders";
import { setGlobalError } from "src/core/shared/store/global/global.actions";

const debug = createDebug("API:REQUEST");

export interface IApiResponse<T, P> {
  params: P | null;
  data: T | null;
  pending: boolean;
  complete: boolean;
  error: IApiError | null;
}

type ApiFuntion<P = any> = <C = P>(P?: C) => void;

export type ApiHookResponse<T, P = any> = [IApiResponse<T, P>, ApiFuntion<P>];

// `async` wrapper to configure `localforage` and instantiate `axios` with `axios-cache-adapter`
const configure = async (req: AxiosRequestConfig) => {
  if (!req.cache) {
    return setup({});
  }

  // Register the custom `memoryDriver` to `localforage`
  await localforage.defineDriver(memoryDriver);

  // Create `localforage` instance
  const forageStore = localforage.createInstance({
    // List of drivers used
    driver: [
      localforage.INDEXEDDB,
      localforage.LOCALSTORAGE,
      memoryDriver._driver
    ],
    // Prefix all storage keys to prevent conflicts
    name: "mezze-ui"
  });

  // Create `axios` instance with pre-configured `axios-cache-adapter` attached to it
  return setup({
  // `axios-cache-adapter` options
    cache: {
      maxAge: 0,
      store: forageStore,
      clearOnError: true,
      key: req => {
        return req.url + queryString.stringify(req.params);
      },
      exclude: { query: false }
    }
  });
};

interface RequestConfig extends AxiosRequestConfig {
  suppressLogsOnCache?: boolean
}

/**
 * useApiRequest -
 *
 * @template D - returned data
 * @template P - post data
 * @param {(params: P) => Promise<D>} asyncApiRequest
 * @param {P} params
 * @returns
 */
const useAxios = <D, P = any>(fn: (params?: any, cache?: Pick<AxiosRequestConfig, "cache">) => AxiosRequestConfig, name: string, axiosOverrides?: AxiosRequestConfig): ApiHookResponse<D, P> => {
  const dispatch = useDispatch();

  const [ res, setRes ] = React.useState<IApiResponse<D, P>>({
    params: null,
    data: null,
    pending: false,
    complete: false,
    error: null
  });

  const [ req, setReq ] = React.useState<{ config: RequestConfig; params: P | any } | null>(null);

  /**
   * Axios response success functionality
   *
   * @param request - The request data
   * @param response  - The response data
   */
  const axiosResponseSuccess = (
    request: {
      config: RequestConfig;
      params: any;
    },
    response: AxiosResponse
  ) => {
    if (response.request.fromCache) {
      if (!request.config.suppressLogsOnCache) {
        debug.log(`${request.config.method} ${name} RESPONSE FROM CACHE %o`, response.data);
      }
    } else {
      debug.log(`${request.config.method} ${name} RESPONSE %o`, response.data);
    }

    // If the response is a 200 HTTP code but the data is not in a
    // JSON API response structure
    if (!response.data.data) {
      const error: AxiosError = {
        toJSON: () => ({}),
        isAxiosError: true,
        name: "Unreachable Server",
        message: "The connection to the server has timeout. Please try again.",
        code: "server_unreachable",
        config: request.config,
        request: response.request,
        response: {
          status: 504,
          statusText: "Gateway Timeout",
          headers: {
            "Content-Type": "application/vnd.api+json",
            "Cache-Control": "private, no-cache, no-store"
          },
          config: response.config,
          data: {
            status: 504,
            errors: [
              {
                status: 504,
                title: "Unreachable Server",
                details: "The connection to the server has timeout. Please try again."
              }
            ]
          },
          request: req
        }
      };

      throw error;
    }

    setRes({
      params: request.params,
      data: response.data.data,
      pending: false,
      error: null,
      complete: true
    });
  };

  /**
   * Axios response success functionality
   *
   * @param request - The request data
   * @param response  - The response data
   */
  const axiosResponseError = (
    request: {
      config: RequestConfig;
      params: any;
    },
    err: AxiosError
  ) => {
    debug.error(`${request.config.method} ${name} %`, err.response?.data);

    if (err.response?.status === 413) {
      Sentry.captureException(err);
    }

    setRes({
      params: request.params,
      data: null,
      pending: false,
      error: apiError(err),
      complete: true
    });
  };

  // On req change
  React.useEffect(() => {
    if (!req) {
      return;
    }

    setRes({
      params: req.params,
      data: null,
      pending: true,
      error: null,
      complete: false
    });

    if (req.config.headers === false || typeof req.config.headers === "object") {
      configure(req.config).then(api =>
        api(req.config)
          .then(response => axiosResponseSuccess(req, response))
          .catch((error: AxiosError) => axiosResponseError(req, error)));
    } else {
      getHeaders(req.config.url)
        .then(headers => {
          if (!req.config.suppressLogsOnCache) {
            debug.log(`${req.config.method} ${name} %o`, req.params, {
              ...req.config,
              headers
            });
          }

          configure(req.config).then(api =>
            api({
              ...req.config,
              headers
            })
              .then(response => axiosResponseSuccess(req, response))
              .catch((error: AxiosError) => axiosResponseError(req, error)));
        })
        .catch(_err => {
          setRes({
            params: req.params,
            data: null,
            pending: false,
            error: {
              status: 401,
              type: "authentication_error",
              title: "Unauthorized",
              message: "There was a problem authorizing this request.",
              data: { path: req.config.url }
            },
            complete: true
          });
        });
    }
  }, [ req ]);

  React.useEffect(() => {
    if (res.complete) {
      if (res.error && res.error.status === 505) {
        // check service worker for updates
        navigator.serviceWorker.getRegistrations()
          .then(registrationsArray => {
            if (registrationsArray[ 0 ]) {
              registrationsArray[ 0 ].update();
            }
          });
      }

      // See if the system is in maintenance mode
      if (res.error && res.error.status === 503 && res.error.type === "api_maintenance_error") {
        dispatch(setGlobalError(res.error));
      }

      // See if the user/customer/drop is suspended
      if (res.error && res.error.status === 488 && res.error.type === "authentication_error_suspended") {
        dispatch(setGlobalError(res.error));
      }
    }
  }, [ res.error ]);

  // prevent request fn from changing every render
  const requestFunction = React.useCallback((params?: unknown, cache?: Pick<AxiosRequestConfig, "cache">) => setReq({
    config: {
      ...fn(params, cache),
      ...axiosOverrides
    },
    params
  }), [ fn, axiosOverrides ]);

  return [ res, requestFunction ];
};

export default useAxios;
